import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import PlayerCard from '../components/PlayerCard';
import DiscoveryFilters from '../components/DiscoveryFilters';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiFilter, FiZap, FiUsers, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

export default function Discovery() {
  const { users, currentUser, friends = [], blockedUsers = [] } = useAppContext();
  const navigate = useNavigate();
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showDesktopFriends, setShowDesktopFriends] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    game: 'All',
    role: [],
    rank: [],
    language: [],
    platform: [],
    type: 'All',
    availability: 'All',
    timezone: 'All',
    lookingForTeam: false
  });

  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Escludi te stesso
      if (user.id === currentUser.id) return false;
      
      // Escludi i giocatori bloccati
      if (blockedUsers.includes(user.id)) return false;

      const matchesSearch = user.nickname.toLowerCase().includes(filters.search.toLowerCase());
      if (!matchesSearch) return false;

      if (filters.language.length > 0) {
        const hasLanguage = user.languages.some(lang => filters.language.includes(lang));
        if (!hasLanguage) return false;
      }

      if (filters.type !== 'All') {
        const hasType = user.games.some(g => g.type === filters.type);
        if (!hasType) return false;
      }

      const matchesGameLogic = user.games.some(g => {
        const gameMatch = filters.game === 'All' || g.name === filters.game;
        if (!gameMatch) return false;

        if (filters.lookingForTeam && !g.lookingForTeam) return false;

        const roleMatch = filters.role.length === 0 || g.roles.some(r => filters.role.includes(r));
        if (!roleMatch) return false;

        const rankMatch = filters.rank.length === 0 || filters.rank.includes(g.rank);
        if (!rankMatch) return false;

        const platformMatch = filters.platform.length === 0 || filters.platform.includes(g.platform);
        if (!platformMatch) return false;

        return true;
      });

      return matchesGameLogic;
    }).sort((a, b) => (a.isOnline === b.isOnline ? 0 : a.isOnline ? -1 : 1));
  }, [users, currentUser.id, filters, blockedUsers]);

  const friendsList = useMemo(() => {
    return users
      .filter(u => friends.includes(u.id))
      .sort((a, b) => (a.isOnline === b.isOnline ? 0 : a.isOnline ? -1 : 1));
  }, [users, friends]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 sm:py-10 flex flex-col lg:flex-row gap-6">
      {/* Desktop Sidebar Filters */}
      <aside className="hidden lg:block w-72 shrink-0">
        <div className="sticky top-24">
          <div className="mb-4 px-1">
            <h2 className="text-lg font-black text-brand-950 dark:text-white flex items-center gap-2 uppercase tracking-tight">
              <SafeIcon icon={FiZap} className="text-brand-600" /> Filters
            </h2>
          </div>
          <div className="bg-white/70 dark:bg-dark-surface/70 backdrop-blur-md border border-white dark:border-dark-border rounded-[32px] overflow-hidden shadow-xl shadow-brand-900/5">
            <DiscoveryFilters filters={filters} setFilters={setFilters} />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h1 className="text-3xl font-black text-brand-950 dark:text-white tracking-tight mb-1 uppercase">Discovery</h1>
            <p className="text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest text-[9px]">Find new Nakama ready for action</p>
          </div>
          <button 
            onClick={() => setShowDesktopFriends(!showDesktopFriends)}
            className="hidden lg:flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-dark-surface border border-gray-100 dark:border-dark-border text-gray-500 hover:text-brand-600 transition-all font-black text-[10px] uppercase tracking-widest shadow-sm"
          >
            <SafeIcon icon={FiUsers} className={showDesktopFriends ? 'text-brand-600' : ''} />
            {showDesktopFriends ? 'Hide Friends' : 'Show Friends'}
            <SafeIcon icon={showDesktopFriends ? FiChevronRight : FiChevronLeft} />
          </button>
        </div>

        <div className="flex gap-3 mb-3">
          <div className="relative flex-1 group">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 group-focus-within:text-brand-600 transition-colors" />
            <input 
              type="text" 
              placeholder="Search by nickname..." 
              className="w-full pl-11 pr-4 py-4 bg-white dark:bg-dark-card border border-gray-100 dark:border-dark-border rounded-2xl shadow-sm focus:outline-none focus:ring-4 focus:ring-brand-500/10 focus:border-brand-500 dark:focus:border-brand-500/50 transition-all font-bold text-sm text-gray-700 dark:text-gray-200"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <button 
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden flex items-center gap-2 px-6 bg-brand-600 text-white rounded-2xl font-black shadow-lg shadow-brand-200 dark:shadow-brand-900/20 active:scale-95 transition-transform"
          >
            <SafeIcon icon={FiFilter} />
          </button>
        </div>

        <div className="mb-8 px-1 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400 rounded-lg border border-brand-100 dark:border-brand-900/30 shadow-sm">
            <span className="text-[10px] font-black uppercase tracking-[0.1em]">
              {filteredUsers.length} Players Found
            </span>
          </div>
          {filters.lookingForTeam && (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-lg border border-amber-100 dark:border-brand-900/30 shadow-sm">
              <SafeIcon icon={FiUsers} className="w-3 h-3" />
              <span className="text-[10px] font-black uppercase tracking-[0.1em]">Recruiting Team Only</span>
            </div>
          )}
        </div>

        {filteredUsers.length > 0 ? (
          <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${showDesktopFriends ? 'xl:grid-cols-3' : 'xl:grid-cols-4'}`}>
            <AnimatePresence mode="popLayout">
              {filteredUsers.map((user) => (
                <motion.div 
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  key={user.id} 
                >
                  <PlayerCard user={user} highlightGame={filters.game !== 'All' ? filters.game : null} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <div className="py-20 text-center bg-white dark:bg-dark-surface rounded-[40px] border border-dashed border-gray-200 dark:border-dark-border">
            <div className="w-16 h-16 bg-gray-50 dark:bg-dark-card rounded-full flex items-center justify-center mx-auto mb-4 text-gray-300 dark:text-gray-600">
              <SafeIcon icon={FiSearch} className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-black text-brand-950 dark:text-white mb-1 uppercase">No results</h3>
            <p className="text-sm text-gray-400 dark:text-dark-muted font-medium">Try adjusting your filters</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showDesktopFriends && (
          <motion.aside 
            initial={{ opacity: 0, x: 20, width: 0 }}
            animate={{ opacity: 1, x: 0, width: 256 }}
            exit={{ opacity: 0, x: 20, width: 0 }}
            className="hidden lg:block shrink-0 overflow-hidden"
          >
            <div className="sticky top-24 w-64">
              <FriendsSidebarContent friendsList={friendsList} navigate={navigate} />
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showMobileFilters && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMobileFilters(false)}
              className="fixed inset-0 bg-brand-950/40 backdrop-blur-md z-[60] lg:hidden"
            />
            <motion.div 
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              className="fixed top-0 right-0 bottom-0 w-[85%] max-w-sm bg-white dark:bg-dark-surface z-[70] lg:hidden shadow-2xl rounded-l-[40px] overflow-hidden"
            >
              <DiscoveryFilters filters={filters} setFilters={setFilters} onClose={() => setShowMobileFilters(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function FriendsSidebarContent({ friendsList, navigate, hideTitle }) {
  return (
    <div className={`bg-white dark:bg-dark-surface rounded-[32px] border border-gray-100 dark:border-dark-border shadow-xl shadow-brand-900/5 overflow-hidden ${hideTitle ? 'rounded-none border-none shadow-none' : ''}`}>
      {!hideTitle && (
        <div className="p-4 border-b border-gray-50 dark:border-dark-border flex items-center justify-between bg-gray-50/30 dark:bg-dark-card/30">
          <h3 className="font-black text-brand-950 dark:text-white text-[11px] uppercase tracking-widest flex items-center gap-2">
            <SafeIcon icon={FiUsers} className="text-brand-600" /> Your Nakama
          </h3>
          <span className="text-[10px] font-bold text-gray-400 dark:text-dark-muted bg-white dark:bg-dark-surface px-2 py-0.5 rounded-full border border-gray-100 dark:border-dark-border">{friendsList.length}</span>
        </div>
      )}
      <div className="p-2 max-h-[500px] overflow-y-auto custom-scrollbar">
        {friendsList.length > 0 ? (
          friendsList.map(friend => (
            <div 
              key={friend.id} 
              onClick={() => navigate(`/profile/${friend.id}`)}
              className="flex items-center gap-3 p-2.5 rounded-2xl hover:bg-brand-50 dark:hover:bg-dark-card cursor-pointer transition-all group"
            >
              <div className="relative shrink-0">
                <img src={friend.avatar} className="w-9 h-9 rounded-xl object-cover border border-gray-100 dark:border-dark-border shadow-sm" alt="" />
                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 border-2 border-white dark:border-dark-surface rounded-full ${friend.isOnline ? 'bg-green-500' : 'bg-gray-300'}`}></span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-brand-950 dark:text-white truncate group-hover:text-brand-600 transition-colors uppercase tracking-tight">{friend.nickname}</p>
                <p className="text-[9px] text-gray-400 dark:text-dark-muted font-bold uppercase tracking-tight">{friend.isOnline ? 'Online' : 'Offline'}</p>
              </div>
            </div>
          ))
        ) : (
          <div className="p-8 text-center">
            <p className="text-[10px] text-gray-400 dark:text-dark-muted font-bold uppercase tracking-widest leading-relaxed">No friends yet</p>
          </div>
        )}
      </div>
    </div>
  );
}